import React from 'react';
import {
  Box,
  TextInput,
  Checkbox,
  DatePicker,
  Button,
  ArrowLeftIcon,
  useToast,
  Dropdown,
  DropdownOverlay,
  SelectInput,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import { useForm } from 'forms';
import { SelectManager } from '../components/SelectManager';
import { SelectOfficeLocation } from './components/SelectOfficeLocation';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from '../components/styles';
import {
  invalidateDraftEmployeeCache,
  updateInitialBasicFormData,
  getBasicDetailsValidationSchema,
} from './utils';
import { initialFormData, employmentTypeOptions } from './data';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';
import { BasicDetailsFormFields } from './types';
import { getPeoplePage } from 'utils/links';
import useFeature from 'hooks/useFeature';
import { useEmployeeSubTypeEnabled } from 'hooks/useEmployeeSubTypeEnabled';
import { PayrollUpdateDraftEmployeeBasicsStepInput } from 'api/gql-utils/employeeOnboardingGql/types';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { getGeneratedEmail } from '../utils';
import { AppError } from 'utils/AppError';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { SelectOfficeLocationV2 } from './components/SelectOfficeLocationV2';

interface BasicDetailsProps {
  setShowDiscardModal: (value: boolean) => void;
  setShowSaveDraftAndCloseModal: (value: boolean) => void;
}

export const BasicDetails = ({
  setShowDiscardModal,
  setShowSaveDraftAndCloseModal,
}: BasicDetailsProps) => {
  const { register, validate, formData, setIsSubmitting, isSubmitting, setFormData } =
    useForm<BasicDetailsFormFields>(initialFormData);

  const queryClient = useQueryClient();

  const toast = useToast();

  const orgId = useSelector(loggedInUserSelectors.organizationID);
  const { isEsicMultiLocationEnabled } = useFeature('esicMultiLocation');

  const { draftEmployeeId, setDraftId } = useDraftEmployeeId();

  const { data, isLoading, isFetching } = useDraftEmployeeData({
    onSuccess: (data) =>
      updateInitialBasicFormData({ data, setFormData, isEsicMultiLocationEnabled }),
  });
  const { isEmployeeOnboardingEnabled } = useFeature('employeeOnboarding');
  const isEmployeeSubTypeEnabled = useEmployeeSubTypeEnabled();
  const history = useHistory();

  const { mutateAsync: createDraftEmployee } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.createDraftEmployee,
    onError: (error: typeof AppError) => {
      setIsSubmitting(false);
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });

  const { mutateAsync: updateDraftEmployeeBasicsStep } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.updateDraftEmployeeBasicsStep,
    onSuccess: () => {
      invalidateDraftEmployeeCache(queryClient, draftEmployeeId);
    },
    onError: (error: typeof AppError) => {
      setIsSubmitting(false);
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });

  const getPayload = (): PayrollUpdateDraftEmployeeBasicsStepInput => {
    return {
      name: formData.employeeName!,
      email: formData.emailNotPresent ? getGeneratedEmail(orgId) : formData.emailId!,
      hireDate: getFormattedDateValue({
        date: formData.hireDate,
        formatString: dateFormats.yearMonthDate,
        filler: '',
      }),
      designation: formData.designation!,
      location: isEsicMultiLocationEnabled ? undefined : Number(formData.officeLocation![0]),
      officeLocationId: isEsicMultiLocationEnabled
        ? Number(formData.officeLocation![0])
        : undefined,
      managerPeopleId: formData.manager_people_id ? Number(formData.manager_people_id) : undefined,
      emailNotPresent: formData.emailNotPresent ?? false,
      department: formData.department ?? '',
      employeeSubType: isEmployeeSubTypeEnabled ? formData.employmentType?.[0] : undefined,
    };
  };

  const handleSubmit = async (e: React.FormEvent, onSuccess: (draftId?: number) => void) => {
    e.preventDefault();
    if (!validate(getBasicDetailsValidationSchema(isEmployeeSubTypeEnabled))) {
      return;
    }
    setIsSubmitting(true);
    if (draftEmployeeId) {
      const res = await updateDraftEmployeeBasicsStep({
        draftEmployeeId,
        input: getPayload(),
      });
      if (res?.payrollUpdateDraftEmployeeBasics?.data?.id) {
        onSuccess(res?.payrollUpdateDraftEmployeeBasics?.data?.id);
      }
    } else {
      const res = await createDraftEmployee(getPayload());
      if (res?.payrollCreateDraftEmployee?.data?.id) {
        setDraftId(res.payrollCreateDraftEmployee.data.id.toString());
        onSuccess(res.payrollCreateDraftEmployee.data.id);
      }
    }
    setIsSubmitting(false);
  };

  const handleSaveAndCloseSuccess = async () => {
    setIsSubmitting(false);
    setShowSaveDraftAndCloseModal(true);
  };

  const handleContinueSuccess = (draftId?: number) => {
    setIsSubmitting(false);
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compensation,
      search: `?draftId=${draftId ?? draftEmployeeId}`,
    });
  };

  const handleStepBack = () => {
    window.location.href = getPeoplePage(isEmployeeOnboardingEnabled);
  };

  const shouldDisableButtons = isLoading || isFetching || isSubmitting;

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />
      <StyledForm onSubmit={(e) => handleSubmit(e, handleContinueSuccess)}>
        <FormFieldsWrapper shouldShowSpinner={isLoading || isFetching}>
          <FormHeading
            title="Basic Details"
            description="Enter the employee's basic information to set up their profile"
          />
          <BorderedBox>
            <TextInput
              autoFocus
              {...register('employeeName')}
              label="Employee Name"
              placeholder="Eg. Roronoa Zoro"
              helpText="The employee's full legal name as it appears on their government ID"
              size="medium"
              necessityIndicator="required"
              isRequired
            />

            <Box>
              <TextInput
                {...register('emailId')}
                label="Employee Email ID"
                placeholder="Eg. rozo@razorpay.com"
                size="medium"
                necessityIndicator={!formData.emailNotPresent ? 'required' : 'none'}
                isDisabled={formData.emailNotPresent}
                type="email"
              />

              <Checkbox
                size="small"
                marginTop="spacing.3"
                {...register('emailNotPresent', 'checkbox')}
                isChecked={!!formData.emailNotPresent}>
                Employee does not have an email address
              </Checkbox>
            </Box>

            <Box position="relative">
              <DatePicker
                {...register('hireDate', 'date')}
                selectionType="single"
                label="Hire Date"
                necessityIndicator="required"
                isRequired
                size="medium"
                helpText="Your employee's first day of work at your company"
              />
            </Box>

            {isEmployeeSubTypeEnabled && (
              <Dropdown selectionType="single">
                <SelectInput
                  {...register('employmentType', 'select')}
                  label="Employment Type"
                  labelPosition="top"
                  size="medium"
                  necessityIndicator="required"
                />
                <DropdownOverlay>
                  <ActionList>
                    {employmentTypeOptions.map((option) => (
                      <ActionListItem
                        key={option.value}
                        title={option.title}
                        description={option.description}
                        value={option.value}
                        isSelected={formData.employmentType?.[0] === option.value}
                      />
                    ))}
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            )}

            <TextInput
              {...register('designation')}
              label="Designation"
              placeholder="Eg. Product Designer"
              size="medium"
              necessityIndicator="required"
              isRequired
            />

            <TextInput
              {...register('department')}
              label="Department"
              placeholder="Eg. Payroll"
              size="medium"
            />

            <SelectManager
              formData={formData}
              registerFormData={register}
              managerName={data?.basicDetails?.managerName}
            />
            {isEsicMultiLocationEnabled ? (
              <SelectOfficeLocationV2 formData={formData} registerFormData={register} />
            ) : (
              <SelectOfficeLocation formData={formData} registerFormData={register} />
            )}
          </BorderedBox>
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            type="button"
            icon={ArrowLeftIcon}
            onClick={handleStepBack}
            isDisabled={shouldDisableButtons}>
            Back
          </Button>
          <Box display="flex" gap="spacing.3">
            <Button
              variant="secondary"
              type="button"
              size="medium"
              onClick={(e) => handleSubmit(e, handleSaveAndCloseSuccess)}
              isLoading={shouldDisableButtons}>
              Save and Close
            </Button>
            <Button type="submit" variant="primary" size="medium" isLoading={shouldDisableButtons}>
              Continue
            </Button>
          </Box>
        </FormFooterWrapper>
      </StyledForm>
    </>
  );
};
